import { LOCALE_NAMES, pickT } from "@repo/shared";
import type { FinancialSituationModel, LocaleCode } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api/_client";
import {
  useCreateFinancialSituationModel,
  useUpdateFinancialSituationModel,
} from "@/lib/hooks/use-financial-situation-models";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./model-dialog.i18n";

type ModelDialogProps = {
  open: boolean;
  model: FinancialSituationModel | null;
  supportedLanguages: string[];
  defaultLanguage: string;
  onClose: () => void;
};

export function ModelDialog({
  open,
  model,
  supportedLanguages,
  defaultLanguage,
  onClose,
}: ModelDialogProps) {
  const t = useT(T);
  const navigate = useNavigate();
  const createMutation = useCreateFinancialSituationModel();
  const updateMutation = useUpdateFinancialSituationModel();
  const isPending = createMutation.isPending || updateMutation.isPending;
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [language, setLanguage] = useState(defaultLanguage);

  useEffect(() => {
    if (open) {
      if (model) {
        setName(model.name);
        setDescription(model.description || "");
        setLanguage(model.language || defaultLanguage);
      } else {
        // The backend always seeds the standard fields — prefill like other template dialogs,
        // in the model's language (the seeded fields use it too), not the user's UI language.
        const modelT = pickT(T, defaultLanguage);
        setName(modelT.templateStandardDefaultName);
        setDescription(modelT.templateStandardDescription);
        setLanguage(defaultLanguage);
      }
      setError(null);
    }
  }, [open, model, defaultLanguage]);

  // Swap the prefilled defaults to the newly picked language, unless the user edited them.
  const handleLanguageChange = (next: string) => {
    const prevT = pickT(T, language);
    const nextT = pickT(T, next);
    if (name === prevT.templateStandardDefaultName) setName(nextT.templateStandardDefaultName);
    if (description === prevT.templateStandardDescription) {
      setDescription(nextT.templateStandardDescription);
    }
    setLanguage(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t.nameRequired);
      return;
    }

    try {
      if (model) {
        await updateMutation.mutateAsync({
          id: model.id,
          data: { name: name.trim(), description: description.trim() || undefined },
        });
      } else {
        const created = await createMutation.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          language,
        });

        // Redirect to configure fields
        if (created.id) {
          navigate(`/settings/financial-situation-models/${created.id}`);
        }
      }

      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(model ? t.failedToUpdateModel : t.failedToCreateModel);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className={model ? undefined : "sm:max-w-[600px]"}>
        <DialogHeader>
          <DialogTitle>{model ? t.editModel : t.createModel}</DialogTitle>
          <DialogDescription>
            {model ? t.editModelDescription : t.createModelDescription}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            {!model && (
              <div className="space-y-3">
                <Label>{t.chooseTemplate}</Label>
                <div className="w-full p-3 rounded-lg border border-primary bg-primary/5 ring-1 ring-primary">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full border-2 border-primary flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      </div>
                      <span className="font-medium">{t.templateStandard}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {t.templateStandardSubtitle}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 ml-6">
                    {t.templateStandardDescription}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">
                {t.name} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.namePlaceholder}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t.descriptionLabel}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t.descriptionPlaceholder}
                rows={3}
              />
            </div>

            {/* Language selector - only shown when creating */}
            {!model && (
              <div className="space-y-2">
                <Label htmlFor="language">
                  {t.language} <span className="text-destructive">*</span>
                </Label>
                <Select value={language} onValueChange={handleLanguageChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectLanguage} />
                  </SelectTrigger>
                  <SelectContent>
                    {supportedLanguages.map((lang: string) => (
                      <SelectItem key={lang} value={lang}>
                        {LOCALE_NAMES[lang as LocaleCode] || lang}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Show language info when editing (read-only) */}
            {model?.language && (
              <div className="space-y-2">
                <Label>{t.language}</Label>
                <p className="text-sm text-muted-foreground">
                  {LOCALE_NAMES[model.language as LocaleCode] || model.language}
                  <span className="ml-2 text-xs">({t.languageCannotBeChanged})</span>
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {model ? t.save : t.create}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
